/**
 * Component: Club Membership Upsell
 * -----------------------------------------------------------------------------
 * Self-contained WebComponent that displays a cashback estimate and opt-in
 * checkbox. When checked, it piggybacks on the product form's add-to-cart
 * flow to also add a club membership variant to the cart.
 *
 * Shows a different message depending on whether the current variant is
 * full-priced or marked down. Switches automatically on variant change
 * using compare_at_price vs price from the variant object.
 *
 * When `data-hide-if-in-cart` is present, the widget automatically hides
 * itself when the membership product is already in the cart, and reappears
 * if it gets removed. Works with the native theme events AND 3rd-party cart
 * drawers (e.g. Upcart) by intercepting `fetch` calls to Shopify cart
 * mutation endpoints.
 *
 * ## Attributes (on the host element):
 *  - data-membership-variant-id        — Shopify variant ID of the membership product
 *  - data-cashback-rate                — Decimal cashback rate (e.g. 0.05 for 5%)
 *  - data-club-discount-rate           — Decimal club member discount rate (e.g. 0.10 for 10%)
 *  - data-current-price                — Initial variant price in cents
 *  - data-is-on-sale                   — "true" / "false" — initial sale state
 *  - data-message-template-full-price  — HTML-escaped richtext; [[ cashback ]] and [[ discount ]]
 *  - data-message-template-markdown    — HTML-escaped richtext; [[ cashback ]]
 *  - data-hide-if-in-cart              — Boolean; enables auto-hide when membership is in cart
 *  - data-hidden                       — Managed by JS; present when the widget is hidden
 *
 * ## Inner selectors:
 *  - [data-js-upsell-checkbox]   — The opt-in checkbox input
 *  - [data-js-upsell-message]    — The container for the rendered message
 *
 * ## Events consumed:
 *  - on:variant:change  — Updates message when the selected variant changes
 *  - on:cart:add        — Triggers membership add-to-cart when checkbox is checked
 *  - on:cart:change     — Re-checks whether membership is already in cart
 *
 * ## Usage:
 * <club-membership-upsell
 *   class="club-membership-upsell"
 *   data-membership-variant-id="12345"
 *   data-cashback-rate="0.05"
 *   data-club-discount-rate="0.10"
 *   data-current-price="14990"
 *   data-is-on-sale="false"
 *   data-message-template-full-price="..."
 *   data-message-template-markdown="..."
 *   data-hide-if-in-cart
 * >
 *   ...
 * </club-membership-upsell>
 */

(() => {
  if (customElements.get('club-membership-upsell')) {
    return;
  }

  /**
   * Regex matching Shopify cart mutation endpoints.
   * Used by the fetch interceptor to detect when ANY script (theme or 3rd-party)
   * modifies the cart, so all instances can re-check visibility.
   */
  const CART_MUTATION_RE =
    /\/cart\/(add|update|change|clear)(\.js|\.json)?(\?|$)/;

  /** @type {Set<ClubMembershipUpsell>} Live instances that need cart-sync */
  const activeInstances = new Set();

  let fetchPatched = false;
  let syncTimer = null;

  /**
   * One-time patch of `window.fetch` to detect cart mutations from any source
   * (theme, Upcart, other 3rd-party apps). After a successful cart mutation,
   * schedules a debounced visibility sync on all active instances.
   */
  function patchFetch() {
    if (fetchPatched) {
      return;
    }

    fetchPatched = true;
    const originalFetch = window.fetch;

    window.fetch = function patchedFetch(...args) {
      const result = originalFetch.apply(this, args);

      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      if (CART_MUTATION_RE.test(url)) {
        result
          .then((response) => {
            if (response.ok) {
              scheduleSync();
            }
          })
          .catch(() => {});
      }

      return result;
    };
  }

  /**
   * Debounced sync — coalesces rapid cart mutations (e.g. Upcart removing +
   * theme dispatching on:cart:change) into a single cart.js fetch per instance.
   */
  function scheduleSync() {
    clearTimeout(syncTimer);

    syncTimer = setTimeout(() => {
      activeInstances.forEach((instance) => instance.syncCartVisibility());
    }, 350);
  }

  class ClubMembershipUpsell extends HTMLElement {
    static _attrs = {
      membershipVariantId: 'data-membership-variant-id',
      cashbackRate: 'data-cashback-rate',
      clubDiscountRate: 'data-club-discount-rate',
      currentPrice: 'data-current-price',
      isOnSale: 'data-is-on-sale',
      messageTemplateFullPrice: 'data-message-template-full-price',
      messageTemplateMarkdown: 'data-message-template-markdown',
      hideIfInCart: 'data-hide-if-in-cart',
      hidden: 'data-hidden',
    };

    static _selectors = {
      checkbox: '[data-js-upsell-checkbox]',
      message: '[data-js-upsell-message]',
    };

    constructor() {
      super();

      /** @type {HTMLInputElement|null} */
      this.checkbox = null;

      /** @type {HTMLElement|null} */
      this.messageEl = null;

      /** @type {string} Richtext template for full-priced variants */
      this.templateFullPrice = '';

      /** @type {string} Richtext template for marked-down variants */
      this.templateMarkdown = '';

      /** @type {number} */
      this.cashbackRate = 0;

      /** @type {number} Club member discount rate (e.g. 0.10) */
      this.clubDiscountRate = 0;

      /** @type {number} Variant price in cents */
      this.currentPrice = 0;

      /** @type {boolean} Whether the current variant is on sale */
      this.isOnSale = false;

      /** @type {string} */
      this.membershipVariantId = '';

      /** @type {boolean} Whether to auto-hide when membership is in cart */
      this.hideIfInCart = false;

      this.onVariantChange = this.handleVariantChange.bind(this);
      this.onCartAdd = this.handleCartAdd.bind(this);
      this.onCartChange = this.handleCartChange.bind(this);
    }

    connectedCallback() {
      this.initialize();
    }

    disconnectedCallback() {
      this.cleanup();
    }

    /**
     * Query elements, read config from attributes, check initial cart state
     */
    initialize() {
      this.checkbox = this.querySelector(
        ClubMembershipUpsell._selectors.checkbox,
      );
      this.messageEl = this.querySelector(
        ClubMembershipUpsell._selectors.message,
      );

      this.membershipVariantId =
        this.getAttribute(ClubMembershipUpsell._attrs.membershipVariantId) ||
        '';
      this.cashbackRate =
        parseFloat(
          this.getAttribute(ClubMembershipUpsell._attrs.cashbackRate),
        ) || 0;
      this.clubDiscountRate =
        parseFloat(
          this.getAttribute(ClubMembershipUpsell._attrs.clubDiscountRate),
        ) || 0;
      this.currentPrice =
        parseInt(
          this.getAttribute(ClubMembershipUpsell._attrs.currentPrice),
          10,
        ) || 0;
      this.isOnSale =
        this.getAttribute(ClubMembershipUpsell._attrs.isOnSale) === 'true';
      this.templateFullPrice =
        this.getAttribute(
          ClubMembershipUpsell._attrs.messageTemplateFullPrice,
        ) || '';
      this.templateMarkdown =
        this.getAttribute(
          ClubMembershipUpsell._attrs.messageTemplateMarkdown,
        ) || '';
      this.hideIfInCart = this.hasAttribute(
        ClubMembershipUpsell._attrs.hideIfInCart,
      );

      this.addEventListeners();

      if (this.hideIfInCart) {
        activeInstances.add(this);
        patchFetch();
        this.syncCartVisibility();
      }
    }

    cleanup() {
      this.removeEventListeners();
      activeInstances.delete(this);
    }

    addEventListeners() {
      const section = this.closest('.js-product');

      if (section) {
        section.addEventListener('on:variant:change', this.onVariantChange);
      }

      document.addEventListener('on:cart:add', this.onCartAdd);
      document.addEventListener('on:cart:change', this.onCartChange);
    }

    removeEventListeners() {
      const section = this.closest('.js-product');

      if (section) {
        section.removeEventListener('on:variant:change', this.onVariantChange);
      }

      document.removeEventListener('on:cart:add', this.onCartAdd);
      document.removeEventListener('on:cart:change', this.onCartChange);
    }

    /**
     * Update price, sale state, and re-render when the variant changes.
     * Uses compare_at_price vs price to determine sale status client-side.
     * @param {CustomEvent} evt - Variant change event with detail.variant
     */
    handleVariantChange(evt) {
      const variant = evt.detail?.variant;

      if (!variant?.price) {
        return;
      }

      this.currentPrice = variant.price;
      this.isOnSale =
        variant.compare_at_price != null &&
        variant.compare_at_price > variant.price;

      this.setAttribute(
        ClubMembershipUpsell._attrs.currentPrice,
        String(variant.price),
      );
      this.setAttribute(
        ClubMembershipUpsell._attrs.isOnSale,
        String(this.isOnSale),
      );

      this.renderMessage();
    }

    /**
     * After the main product is added to cart, add the membership if checked
     * @param {CustomEvent} _evt - Cart add event
     */
    async handleCartAdd(_evt) {
      if (!this.checkbox?.checked || !this.membershipVariantId) {
        return;
      }

      this.checkbox.checked = false;

      try {
        await this.addMembershipToCart();
        this.refreshCartSections();
      } catch (error) {
        console.error(
          '[ClubMembershipUpsell] Failed to add membership:',
          error,
        );
      }
    }

    /**
     * When the theme fires on:cart:change, re-check cart visibility.
     * The fetch interceptor handles 3rd-party cart drawers separately.
     */
    handleCartChange() {
      if (this.hideIfInCart) {
        this.syncCartVisibility();
      }
    }

    /**
     * Fetch the cart JSON and show/hide based on membership presence
     */
    async syncCartVisibility() {
      if (!this.membershipVariantId || !this.hideIfInCart) {
        return;
      }

      try {
        const response = await fetch(`${theme.routes.cart}.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();

        const membershipInCart = cart.items.some(
          (item) => String(item.variant_id) === this.membershipVariantId,
        );

        this.toggleHidden(membershipInCart);
      } catch (error) {
        console.error('[ClubMembershipUpsell] Failed to check cart:', error);
      }
    }

    /**
     * Show or hide the widget via the data-hidden attribute
     * @param {boolean} hidden - Whether the widget should be hidden
     */
    toggleHidden(hidden) {
      if (hidden) {
        this.setAttribute(ClubMembershipUpsell._attrs.hidden, '');
      } else {
        this.removeAttribute(ClubMembershipUpsell._attrs.hidden);
      }
    }

    /**
     * POST the membership variant to the cart add endpoint
     * @returns {Promise<object>} Cart API response
     */
    async addMembershipToCart() {
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: this.membershipVariantId,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart add failed: ${response.status}`);
      }

      return response.json();
    }

    /**
     * Dispatch a cart change event so header/drawer update their counts
     */
    refreshCartSections() {
      document.dispatchEvent(
        new CustomEvent('on:cart:change', {
          bubbles: true,
          cancelable: false,
        }),
      );
    }

    /**
     * Pick the correct template based on sale state and replace placeholders
     */
    renderMessage() {
      if (!this.messageEl) {
        return;
      }

      const template = this.isOnSale
        ? this.templateMarkdown
        : this.templateFullPrice;

      if (!template) {
        return;
      }

      const cashback = this.formatMoney(
        Math.round(this.currentPrice * this.cashbackRate),
      );
      const discount = this.formatMoney(
        Math.round(this.currentPrice * this.clubDiscountRate),
      );

      this.messageEl.innerHTML = template
        .replace(/\[\[ cashback \]\]/g, cashback)
        .replace(/\[\[ discount \]\]/g, discount);
    }

    /**
     * Format cents into a dollar string using the store's currency symbol
     * @param {number} cents - Amount in cents
     * @returns {string} Formatted price (e.g. "$7.49")
     */
    formatMoney(cents) {
      const sign = window.theme?.currencySign || '$';
      const amount = (cents / 100).toFixed(2);

      return `${sign}${amount}`;
    }
  }

  customElements.define('club-membership-upsell', ClubMembershipUpsell);
})();
